<template>
  <router-view v-slot="{ Component, route }">
    <component :is="transfer(Component, route)" :no-cache-page="true" />
  </router-view>
  <div class="router-view-container">
    <div
      class="router-page"
      :class="{ active: activeKey === item.cacheid }"
      v-for="item in cacheList"
      :key="item.cacheid"
    >
      <component :is="item.component" :route="item.route" :page-cacheid="item.cacheid" />
    </div>
  </div>
</template>

<script>
import {
  computed,
  getCurrentInstance,
  inject,
  onUnmounted,
  provide,
  reactive,
  ref,
  watch,
} from 'vue';
import { matchedRouteKey, useRoute } from 'vue-router';
import { debounce } from 'lodash';

let resizeRecord = 0;
window.addEventListener(
  'resize',
  debounce(
    () => {
      resizeRecord = new Date().getTime();
    },
    300,
    {
      leading: true,
    },
  ),
);

function getOwnerPage() {
  let ins = getCurrentInstance();
  while (ins) {
    if (ins.attrs['page-cacheid'] || ins.attrs['no-cache-page']) {
      break;
    }
    ins = ins.parent;
  }
  return ins;
}

export function usePageShow(fn, options = {}) {
  const ins = getCurrentInstance();
  if (ins) {
    const useActivePageFlag = inject('useActivePageFlag', false);
    if (!useActivePageFlag) {
      console.warn('usePageShow所在的组件不属于任何page');
      return;
    }
    const isActive = useActivePageFlag();
    if (isActive.value === 'no-cache') {
      // console.warn('本页面不缓存,无须使用usePageShow');
      return;
    }

    let selfResizeRecord = 0;
    const unwatch = watch(isActive, (bool) => {
      if (bool === true) {
        const opt = {
          resized: selfResizeRecord !== resizeRecord,
        };
        selfResizeRecord = resizeRecord;
        fn(opt);
        if (options.once) {
          unwatch();
        }
      }
    });
  } else {
    console.error('usePageShow必须在setup或生命周期中同步调用');
  }
}

export function useResized(fn) {
  const ins = getCurrentInstance();
  if (ins) {
    const wrapFn = debounce(fn, 300);
    onUnmounted(() => {
      window.removeEventListener('resize', wrapFn);
    });
    const useActivePageFlag = inject('useActivePageFlag', false);
    if (!useActivePageFlag) {
      console.warn('useResized所在的组件不属于任何page');
      return;
    }
    const isActive = useActivePageFlag();

    if (isActive.value === 'no-cache') {
      window.addEventListener('resize', wrapFn);
      return;
    }

    watch(
      isActive,
      (bool) => {
        if (bool) {
          window.addEventListener('resize', wrapFn);
        } else {
          window.removeEventListener('resize', wrapFn);
        }
      },
      {
        immediate: true,
      },
    );

    const usePageShow1 = inject('usePageShow');
    usePageShow1(fn);
  } else {
    console.error('useResized必须在setup或生命周期中同步调用');
  }
}

function createCache() {
  // 最大缓存数量
  const max = 15;
  const cacheList = reactive([]);
  const activeKey = ref('');

  // cache中是否已经存在对应实例
  function getCache(cacheid) {
    return cacheList.find((item) => item.cacheid === cacheid);
  }

  // 激活实例.
  function activeCache(cache) {
    activeKey.value = cache.cacheid;
  }

  // 增加一个实例
  function addCache(component, cacheid, route) {
    const cache = {
      component,
      cacheid,
      route,
    };
    cacheList.push(cache);

    if (cacheList.length > max) {
      // @TODO 按最近激活序列,使用频次,时长
      cacheList.shift();
    }
    return cache;
  }

  // 销毁若干实例
  function clearCache(list) {
    const toClear = Array.isArray(list) ? list : [list];
    toClear.forEach((cacheid) => {
      const index = cacheList.findIndex((cache) => cache.cacheid === cacheid);
      if (index >= 0) {
        cacheList.splice(index, 1);
      }
    });
  }

  return {
    cacheList,
    activeKey,
    getCache,
    addCache,
    activeCache,
    clearCache,
  };
}

export default {
  setup() {
    const {
      cacheList, getCache, addCache, activeCache, activeKey, clearCache,
    } = createCache();

    const routeRef = ref({});
    provide(matchedRouteKey, routeRef);

    function useActivePageFlag() {
      if (!getCurrentInstance()) {
        console.error('useActivePageFlag必须在setup或生命周期同步调用');
        return ref(false);
      }
      const ownerPage = getOwnerPage();
      if (ownerPage.attrs['no-cache-page']) {
        return ref('no-cache');
      }
      return computed(() => ownerPage.attrs['page-cacheid'] === activeKey.value);
    }
    provide('useActivePageFlag', useActivePageFlag);

    const transMap = new Map();
    function transfer(Component, r) {
      // Component是vnode!!!导航守卫已经注册到router上了,所以可以直接在原组件上直接用组件内导航守卫(选项)

      // 指定无缓存,或者有子路由则直接不缓存(用原生路由)
      if (r.meta?.noCache || r.matched.length > 1) {
        activeKey.value = '';
        return Component;
      }

      if (!transMap.get(Component.type)) {
        transMap.set(Component.type, {
          setup() {
            // 动态设置matchedRouteKey,使得导航守卫(onBeforeRouteLeave和onBeforeRouteUpdate)可以准确定位到对应路由
            routeRef.value = inject(matchedRouteKey, {}).value;

            const route = useRoute();

            // @TODO 临时方案,自动清理缓存,使得默认不缓存
            const saveList = route.meta?.cachePage || [];
            const cacheIds = cacheList.map((cache1) => cache1.cacheid);
            clearCache(cacheIds.filter((id) => !saveList.includes(id)));

            // 暂时用fullPath作为唯一标识
            const cacheid = route.fullPath;
            let cache = getCache(cacheid);

            if (cache) {
              activeCache(cache);
            } else {
              cache = addCache(Component, cacheid, route);
              activeCache(cache);
            }

            return () => null;
          },
        });
      }
      return transMap.get(Component.type);
    }
    return {
      transfer,
      cacheList,
      activeKey,
    };
  },
};
</script>

<style lang="scss" scoped>
.router-view-container {
  position: relative;
  .router-page {
    width: 100%;
    // background: var(--g9);
    position: absolute;
    transform: translate3d(-100000px, -100000px, -1px);
    &.active {
      position: relative;
      transform: none;
    }
  }
}
</style>
